import { useState } from 'react';
import { useCookieConsent } from '../contexts/CookieContext';

/**
 * GDPR-compliant Cookie Consent Banner Component
 * @returns {JSX.Element|null} Cookie consent banner or null if not needed
 */
const CookieConsentBanner = () => {
  const { showBanner, acceptAll, rejectAll, updateConsent } =
    useCookieConsent();
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  if (!showBanner) {
    return null;
  }

  /**
   * Handle preference toggle
   * @param {string} key - Preference key
   */
  const handleToggle = (key) => {
    if (key === 'necessary') return; // Cannot toggle necessary cookies

    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  /**
   * Save custom preferences
   */
  const handleSavePreferences = () => {
    updateConsent(preferences);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center pointer-events-none">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50 pointer-events-auto" />

      {/* Banner */}
      <div className="relative w-full max-w-4xl m-4 bg-white rounded-lg shadow-2xl pointer-events-auto animate-slide-up">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-gray-900">
                Patriot Claims Values Your Privacy
              </h2>
            </div>
          </div>

          {/* Main Content */}
          {!showDetails ? (
            <>
              <p className="text-gray-600 mb-6">
                We use cookies to enhance your browsing experience, serve
                personalized content, and analyze our traffic. By clicking
                "Accept All", you consent to our use of cookies.
              </p>

              <div className="flex flex-wrap gap-3">
                <button
                  onClick={acceptAll}
                  className="px-6 py-2.5 bg-primary text-white font-medium rounded-lg hover:bg-primary/80 transition-colors"
                >
                  Accept All
                </button>

                <button
                  onClick={rejectAll}
                  className="px-6 py-2.5 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Reject All
                </button>

                <button
                  onClick={() => setShowDetails(true)}
                  className="px-6 py-2.5 border-2 border-gray-300 text-gray-700 font-medium rounded-lg hover:border-gray-400 transition-colors"
                >
                  Customize
                </button>
              </div>

              <p className="text-sm text-gray-500 mt-4">
                Read our{' '}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://patriotclaims.com/privacy-policy/"
                  className="text-primary hover:underline"
                >
                  Privacy Policy
                </a>
              </p>
            </>
          ) : (
            <>
              <p className="text-gray-600 mb-6">
                Choose which cookies you want to accept. You can change these
                settings at any time.
              </p>

              {/* Cookie Categories */}
              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-gray-900">
                        Necessary Cookies
                      </h3>
                      <span className="px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-700 rounded">
                        Always Active
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Essential for the website to function properly. These
                      cookies enable basic functions like page navigation and
                      access to secure areas.
                    </p>
                  </div>
                  <div class="ml-4">
                    <button
                      class="w-12 h-6 rounded-full transition-colors bg-primary opacity-50 cursor-not-allowed"
                      aria-label="Toggle analytics cookies"
                    >
                      <div class="w-5 h-5 bg-white rounded-full transform transition-transform m-0.5 translate-x-6"></div>
                    </button>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-2">
                      Analytics Cookies
                    </h3>
                    <p className="text-sm text-gray-600">
                      Help us understand how visitors interact with our website
                      by collecting and reporting information anonymously. Used
                      to improve user experience.
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => handleToggle('analytics')}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        preferences.analytics ? 'bg-primary' : 'bg-gray-300'
                      }`}
                      aria-label="Toggle analytics cookies"
                    >
                      <div
                        className={`w-5 h-5 bg-white rounded-full transform transition-transform m-0.5 ${
                          preferences.analytics
                            ? 'translate-x-6'
                            : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-2">
                      Marketing Cookies
                    </h3>
                    <p className="text-sm text-gray-600">
                      Used to track visitors across websites to display relevant
                      advertisements and encourage engagement with our services.
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => handleToggle('marketing')}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        preferences.marketing ? 'bg-primary' : 'bg-gray-300'
                      }`}
                      aria-label="Toggle marketing cookies"
                    >
                      <div
                        className={`w-5 h-5 bg-white rounded-full transform transition-transform m-0.5 ${
                          preferences.marketing
                            ? 'translate-x-6'
                            : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleSavePreferences}
                  className="px-6 py-2.5 bg-primary text-white font-medium rounded-lg hover:bg-primary/80 transition-colors"
                >
                  Save Preferences
                </button>

                <button
                  onClick={acceptAll}
                  className="px-6 py-2.5 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Accept All
                </button>

                <button
                  onClick={() => setShowDetails(false)}
                  className="px-6 py-2.5 border-2 border-gray-300 text-gray-700 font-medium rounded-lg hover:border-gray-400 transition-colors"
                >
                  Back
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
